import { Button } from '@automata/ui';
import { logger } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import {
  useGetPlanResult,
  useSaveWorkflow,
  useWorkflowPlan,
} from '@automata/wfb-api';
import { PlannerOptionsInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { SettingsOutlined } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Content from './Content';
import useIsPlanWorkflowModalOpen from './IsPlanWorkflowModalOpenContext/useIsPlanWorkflowModalOpen';
import PlanWorkflowForm from './PlanWorkflowForm';
import { PlanWorkflowModal } from './PlanWorkflowModal';
import { getPreviewState, isNotNullish, transformData } from './utils';
import { setBatchesForSavingWorkflow } from '../../features/actions';
import { setWorkflowIsSaved } from '../../features/ui/uiSlice';
import {
  selectLastPlanningTime,
  selectParameterValues,
} from '../../features/workflow/selectors';
import {
  setWorkflowMetadata,
  updateLastPlanningTime,
  updatePlannerOptions,
} from '../../features/workflow/workflowSlice';
import {
  selectWorkflowJsonToPreview,
  selectWorkflowJsonToSave,
} from '../../features/workflowJsonSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PreviewBlocker } from '../../text';
import { getParameterValues } from '../../utils';
import { ConnectedWorkflowDescriptionPanel } from '../GraphEditorToolbars/WorkflowDescriptionPanel';
import PreviewToolbar from '../PreviewToolbar';
import PreviewMetrics from '../PreviewToolbar/PreviewMetrics';
import { zIndexValues } from '../styles';

interface PreviewPanelProps {
  workflowId?: string;
  onClickReturnToWorkflow: () => void;
  isDescriptionPanelExpanded?: boolean;
}

// Sidebar width constants
const SIDEBAR_MIN_WIDTH = 320;
const SIDEBAR_MAX_WIDTH = 400;
const SIDEBAR_PERCENTAGE = '20%';
const SIDEBAR_WIDTH = `clamp(${SIDEBAR_MIN_WIDTH}px, ${SIDEBAR_PERCENTAGE}, ${SIDEBAR_MAX_WIDTH}px)`;

type SidebarToggleButtonProps = {
  isCollapsed: boolean;
  onClick: () => void;
  position: {
    top: string;
    right: string;
  };
  zIndex: number;
};

const SidebarToggleButton = ({
  isCollapsed,
  onClick,
  position,
  zIndex,
}: SidebarToggleButtonProps) => (
  <Box
    sx={{
      position: 'absolute',
      top: position.top,
      right: position.right,
      zIndex,
      transition: 'right 0.3s ease',
    }}
  >
    <Tooltip
      title={
        isCollapsed
          ? 'Expand Configuration Panel'
          : 'Collapse Configuration Panel'
      }
      placement="left"
    >
      <IconButton
        onClick={onClick}
        sx={{
          bgcolor: 'common.white',
          boxShadow: 2,
          p: 1,
          borderRadius: isCollapsed ? 1 : '4px 0 0 4px',
        }}
        size="small"
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <SettingsOutlined />
          {isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </Stack>
      </IconButton>
    </Tooltip>
  </Box>
);

const PreviewPanel = ({
  workflowId,
  onClickReturnToWorkflow,
  isDescriptionPanelExpanded,
}: PreviewPanelProps) => {
  const saveToFetchMaxBatches = useRef(false);
  const useLatestPlanAsHintRef = useRef(false);
  const isPlanningOptionsEnabled = useFlag('fe.planning_options');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [isPlanWorkflowModalOpen, setIsPlanWorkflowModalOpen] =
    useIsPlanWorkflowModalOpen();

  const router = useRouter();
  const dispatch = useAppDispatch();
  const parameters = useAppSelector(selectParameterValues);
  const workflowJsonPreview = useAppSelector(selectWorkflowJsonToPreview);
  const workflowJsonToSave = useAppSelector(selectWorkflowJsonToSave);
  const lastPlanningTime = useAppSelector(selectLastPlanningTime);

  const {
    requestNewPlan,
    isIdle: workflowPlanIsIdle,
    result: { data: workflowPlanData, error: workflowPlanError },
  } = useWorkflowPlan(workflowId);

  const {
    saveWorkflow,
    isIdle: saveWorkflowIsIdle,
    result: { error: saveWorkflowError },
  } = useSaveWorkflow({
    onSuccess: async (data) => {
      dispatch(setWorkflowMetadata(data));

      if (!saveToFetchMaxBatches.current) {
        try {
          await requestNewPlan({
            queryParams: {
              workflow_id: data.id,
              ...(useLatestPlanAsHintRef.current && {
                hint_plan_id: 'latest',
              }),
            },
            body: { parameter_values: getParameterValues(parameters) },
          });
        } catch (error) {
          logger.error(error);
          dispatch(setWorkflowIsSaved(false));
          return;
        }
      }

      const isExistingWorkflow = !!workflowId;
      if (!isExistingWorkflow) {
        router.replace({
          query: { ...router.query, initialSave: true, id: data.id },
        });
      }

      if (saveToFetchMaxBatches.current) {
        setIsPlanWorkflowModalOpen(true);
        saveToFetchMaxBatches.current = false;
      }
    },
  });

  const {
    data: planResult,
    fetchStatus: planResultFetchStatus,
    error: planResultError,
  } = useGetPlanResult(
    {
      pathParams: {
        workflowId: workflowId ?? '',
        planId: workflowPlanData?.id ?? '',
      },
    },
    {
      staleTime: Infinity,
      enabled:
        !!workflowId &&
        !!workflowPlanData?.id &&
        (workflowPlanData?.status === 'COMPLETED' ||
          (workflowPlanData?.status === 'CANCELLED' &&
            workflowPlanData?.result_available === true)),
    }
  );

  useEffect(() => {
    if (
      planResult &&
      planResult.metrics.plan_solve_duration !== lastPlanningTime
    ) {
      dispatch(updateLastPlanningTime(planResult.metrics.plan_solve_duration));
    }
  }, [lastPlanningTime, planResult, dispatch]);

  const handleCreatePlan = useCallback(
    ({
      batches,
      optimisationTarget,
    }: {
      batches: number;
      optimisationTarget: PlannerOptionsInput['optimisation_target'];
    }) => {
      setIsPlanWorkflowModalOpen(false);

      dispatch(
        updatePlannerOptions({
          optimisation_target: optimisationTarget,
        })
      );
      const workflowToSave = dispatch(setBatchesForSavingWorkflow({ batches }));
      saveWorkflow(workflowToSave, workflowId);
      dispatch(setWorkflowIsSaved(true));
    },
    [workflowId, dispatch, saveWorkflow, setIsPlanWorkflowModalOpen]
  );

  const handleClickCreatePlan = useCallback(() => {
    saveToFetchMaxBatches.current = true;

    // We only save here to determine max batches. The actual save is done in the modal
    const workflowToSave = dispatch(
      setBatchesForSavingWorkflow({ batches: 1 })
    );
    saveWorkflow(workflowToSave, workflowId);
    dispatch(setWorkflowIsSaved(true));
  }, [workflowId, dispatch, saveWorkflow]);

  const [formState, setFormState] = useState<
    | {
        batches: number | null;
        optimisationTarget: NonNullable<
          PlannerOptionsInput['optimisation_target']
        >;
        isParametersValid: boolean;
        isBatchValid: boolean;
        blockers: PreviewBlocker[];
        useLatestPlanAsHint: boolean;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    useLatestPlanAsHintRef.current = formState?.useLatestPlanAsHint ?? false;
  }, [formState?.useLatestPlanAsHint]);

  const isPlanAndPreviewDisabled = useMemo(() => {
    if (workflowJsonPreview.isError) return true;

    if (!formState) return true;

    const hasBlockers = formState.blockers && formState.blockers.length > 0;

    return (
      !formState.isParametersValid || !formState.isBatchValid || hasBlockers
    );
  }, [formState, workflowJsonPreview.isError]);

  const handlePlanAndPreview = useCallback(() => {
    if (!formState) return;

    const { batches, optimisationTarget } = formState;
    const parsedBatches = Number(batches);

    if (isNaN(parsedBatches)) return;

    dispatch(updatePlannerOptions({ optimisation_target: optimisationTarget }));

    const workflowToSave = dispatch(
      setBatchesForSavingWorkflow({ batches: parsedBatches })
    );

    saveWorkflow(workflowToSave, workflowId);

    dispatch(setWorkflowIsSaved(true));
  }, [dispatch, formState, saveWorkflow, workflowId]);

  if (isPlanningOptionsEnabled) {
    return (
      <Box
        // Uses the same absolute positioning trick as MainPanel to allow
        // independent scrolling of side panel
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'grey.100',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <Stack
            direction="column"
            sx={{
              minWidth: 0,
              flex: 1,
              height: '100%',
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{ minWidth: 0, flexShrink: 0 }}
            >
              <PreviewMetrics
                estimatedRuntime={planResult?.metrics.total_plan_duration ?? 0}
                batches={planResult?.metrics.batches_requested ?? null}
              />
            </Stack>

            <Box flex={1} minWidth={0} overflow="auto" minHeight={0}>
              <Content
                workflowJson={workflowJsonPreview}
                fetchErrors={[
                  workflowPlanError,
                  saveWorkflowError,
                  planResultError,
                ].filter(isNotNullish)}
                isLoading={
                  planResultFetchStatus === 'fetching' ||
                  (workflowPlanData === undefined && !workflowPlanIsIdle)
                }
                previewState={getPreviewState(
                  workflowJsonPreview,
                  workflowPlanData
                )}
                runProgressData={planResult ? transformData(planResult) : null}
                onClickReturnToWorkflow={onClickReturnToWorkflow}
                planId={workflowPlanData?.id || ''}
                stageDetail={workflowPlanData?.stage_detail}
                stoppedEarly={workflowPlanData?.status === 'CANCELLED'}
              />
            </Box>

            <Box
              bgcolor="common.white"
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePlanAndPreview}
                loading={
                  !workflowPlanIsIdle ||
                  !saveWorkflowIsIdle ||
                  workflowPlanData?.status === 'PENDING'
                }
                disabled={isPlanAndPreviewDisabled}
              >
                Plan and Preview
              </Button>
            </Box>
          </Stack>
        </Box>

        {/* Configuration Drawer */}
        <Drawer
          anchor="right"
          open={!isCollapsed}
          onClose={() => setIsCollapsed(true)}
          variant="persistent"
          sx={{
            width: !isCollapsed ? SIDEBAR_WIDTH : 0,
            flexShrink: 0,
            transition: 'width 0.3s ease',
            '& .MuiDrawer-paper': {
              width: 'auto',
              position: 'relative',
              borderLeft: '1px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              px: 1,
              py: 2,
            }}
          >
            <PlanWorkflowForm onChange={setFormState} />
          </Box>
        </Drawer>

        {/* Toggle Button - positioned outside the drawer */}
        <SidebarToggleButton
          isCollapsed={isCollapsed}
          onClick={() => setIsCollapsed(!isCollapsed)}
          position={{
            top: '16px',
            right: isCollapsed ? '-6px' : `calc(${SIDEBAR_WIDTH} - 4px)`,
          }}
          zIndex={isCollapsed ? 1201 : 1199}
        />

        {/* Floating Description Panel */}
        {isDescriptionPanelExpanded && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: zIndexValues.toolbars,
              pointerEvents: 'auto',
            }}
          >
            <ConnectedWorkflowDescriptionPanel />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <PreviewToolbar
        workflowJson={workflowJsonPreview}
        onClickCreatePlan={handleClickCreatePlan}
        disabledCreatePlan={workflowJsonPreview.isError}
        oldPlanExists={planResult !== undefined}
        isLoadingCreatePlan={
          !workflowPlanIsIdle ||
          !saveWorkflowIsIdle ||
          workflowPlanData?.status === 'PENDING'
        }
        estimatedRuntime={planResult?.metrics.total_plan_duration ?? 0}
        batches={planResult?.metrics.batches_requested ?? null}
      />
      {isPlanWorkflowModalOpen && (
        <PlanWorkflowModal
          label="Plan"
          workflowName={workflowJsonToSave.name}
          onSubmit={handleCreatePlan}
          onClose={() => setIsPlanWorkflowModalOpen(false)}
        />
      )}
      <Content
        workflowJson={workflowJsonPreview}
        fetchErrors={[
          workflowPlanError,
          saveWorkflowError,
          planResultError,
        ].filter(isNotNullish)}
        isLoading={
          planResultFetchStatus === 'fetching' ||
          (workflowPlanData === undefined && !workflowPlanIsIdle)
        }
        previewState={getPreviewState(workflowJsonPreview, workflowPlanData)}
        runProgressData={planResult ? transformData(planResult) : null}
        onClickReturnToWorkflow={onClickReturnToWorkflow}
        planId={workflowPlanData?.id || ''}
        stageDetail={workflowPlanData?.stage_detail}
        stoppedEarly={workflowPlanData?.status === 'CANCELLED'}
      />

      {/* Floating Description Panel */}
      {isDescriptionPanelExpanded && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: zIndexValues.toolbars,
            pointerEvents: 'auto',
          }}
        >
          <ConnectedWorkflowDescriptionPanel />
        </Box>
      )}
    </>
  );
};

export default PreviewPanel;
